import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';

const DEFAULT_FILTERS = {
  search: '',
  status: 'all',
  runStatus: 'all',
  runType: 'all',
  executedBy: '',
  buildNumber: '',
  environment: '',
  platform: '',
  browser: '',
  appVersion: '',
  dateFrom: '',
  dateTo: '',
  sortBy: 'executedAt',
  sortOrder: 'desc',
  page: 1,
  limit: 50,
};

const RESULT_STATUSES = ['all', 'not_started', 'in_progress', 'passed', 'failed', 'blocked', 'skipped'];
const RUN_STATUSES = ['all', 'not_started', 'in_progress', 'completed', 'aborted'];
const RUN_TYPES = ['all', 'manual', 'automated'];
const STATUS_META = {
  not_started: { label: 'Not Run', className: 'bg-secondary' },
  in_progress: { label: 'In Progress', className: 'bg-info text-dark' },
  passed: { label: 'Passed', className: 'bg-success' },
  failed: { label: 'Failed', className: 'bg-danger' },
  blocked: { label: 'Blocked', className: 'bg-warning text-dark' },
  skipped: { label: 'Skipped', className: 'bg-dark' },
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function StatCard({ label, value, tone = 'primary' }) {
  return (
    <div className="col-md-2 col-sm-4 col-6">
      <div className={`card border-${tone} h-100`}>
        <div className="card-body py-3">
          <div className="text-muted small">{label}</div>
          <div className={`fs-4 fw-bold text-${tone}`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function TestResults() {
  const { projectKey, runId } = useParams();
  const [project, setProject] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 50 });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [savedPresets, setSavedPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [presetName, setPresetName] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const scopeLabel = runId
    ? 'Test Run Results'
    : projectKey
      ? 'Project Test Results'
      : 'Global Test Results';

  const breadcrumbBase = projectKey ? `/projects/${projectKey}` : null;
  const presetStorageKey = useMemo(() => {
    if (runId) return `zipqa:test-results:presets:run:${runId}`;
    if (projectKey) return `zipqa:test-results:presets:project:${projectKey}`;
    return 'zipqa:test-results:presets:global';
  }, [projectKey, runId]);

  const getPersistedFilters = useCallback((source = filters) => {
    const { page, ...rest } = source;
    return { ...rest, page: 1 };
  }, [filters]);

  const fetchProject = useCallback(async () => {
    if (!projectKey) return null;
    const res = await api.get(`/projects/${projectKey}`);
    const data = res.data?.data || res.data || null;
    setProject(data);
    return data;
  }, [projectKey]);

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let resolvedProjectId = project?._id || project?.id || null;
      if (projectKey && !resolvedProjectId) {
        const nextProject = await fetchProject();
        resolvedProjectId = nextProject?._id || nextProject?.id || null;
      }

      const endpoint = runId ? `/runs/${runId}/results` : '/runs/results/global';
      const params = {
        ...filters,
        ...(projectKey && resolvedProjectId ? { projectId: resolvedProjectId } : {}),
      };

      const res = await api.get(endpoint, { params });
      setResults(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0, limit: filters.limit });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch test results');
    } finally {
      setLoading(false);
    }
  }, [fetchProject, filters, project?._id, project?.id, projectKey, runId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(presetStorageKey) || '[]');
      setSavedPresets(Array.isArray(stored) ? stored : []);
    } catch {
      setSavedPresets([]);
    }
  }, [presetStorageKey]);

  const summary = useMemo(() => ({
    total: results.length,
    passed: results.filter(result => result.status === 'passed').length,
    failed: results.filter(result => result.status === 'failed').length,
    blocked: results.filter(result => result.status === 'blocked').length,
    skipped: results.filter(result => result.status === 'skipped').length,
    notRun: results.filter(result => result.status === 'not_started').length,
  }), [results]);

  const setFilter = (key, value) => {
    setMessage('');
    setFilters(prev => ({ ...prev, [key]: value, page: key === 'page' ? value : 1 }));
  };

  const toggleSort = (sortBy) => {
    setMessage('');
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  };

  const resetFilters = () => {
    setSelectedPreset('');
    setPresetName('');
    setMessage('');
    setFilters(DEFAULT_FILTERS);
  };

  const savePreset = () => {
    const name = presetName.trim();
    if (!name) {
      setMessage('Please enter a name before saving the filter preset.');
      return;
    }

    const nextPreset = {
      id: selectedPreset || `${Date.now()}`,
      name,
      filters: getPersistedFilters(),
      updatedAt: new Date().toISOString(),
    };

    const withoutCurrent = savedPresets.filter(preset => preset.id !== nextPreset.id && preset.name !== name);
    const nextPresets = [nextPreset, ...withoutCurrent].sort((left, right) => left.name.localeCompare(right.name));
    localStorage.setItem(presetStorageKey, JSON.stringify(nextPresets));
    setSavedPresets(nextPresets);
    setSelectedPreset(nextPreset.id);
    setMessage(`Saved filter preset “${name}”.`);
  };

  const applyPreset = (presetId) => {
    setSelectedPreset(presetId);
    if (!presetId) return;
    const preset = savedPresets.find(item => item.id === presetId);
    if (!preset) return;
    setPresetName(preset.name);
    setFilters({ ...DEFAULT_FILTERS, ...preset.filters, page: 1 });
    setMessage(`Applied filter preset “${preset.name}”.`);
  };

  const deletePreset = () => {
    if (!selectedPreset) {
      setMessage('Select a saved preset to delete.');
      return;
    }
    const preset = savedPresets.find(item => item.id === selectedPreset);
    const nextPresets = savedPresets.filter(item => item.id !== selectedPreset);
    localStorage.setItem(presetStorageKey, JSON.stringify(nextPresets));
    setSavedPresets(nextPresets);
    setSelectedPreset('');
    setPresetName('');
    setMessage(`Deleted filter preset “${preset?.name || 'Untitled'}”.`);
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      setError('');
      setMessage('');

      let resolvedProjectId = project?._id || project?.id || null;
      if (projectKey && !resolvedProjectId) {
        const nextProject = await fetchProject();
        resolvedProjectId = nextProject?._id || nextProject?.id || null;
      }

      const endpoint = runId ? `/runs/${runId}/results` : '/runs/results/global';
      const params = {
        ...getPersistedFilters(),
        format: 'csv',
        ...(projectKey && resolvedProjectId ? { projectId: resolvedProjectId } : {}),
      };

      const response = await api.get(endpoint, { params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const scope = runId ? `run-${runId}` : projectKey ? `project-${projectKey}` : 'global-results';
      link.href = url;
      link.setAttribute('download', `${scope}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('CSV export downloaded.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export filtered results');
    } finally {
      setExporting(false);
    }
  };

  const getRunLink = result => {
    if (projectKey) return `/projects/${projectKey}/runs/${result.runId}`;
    return `/runs/${result.runId}`;
  };

  const getRunResultsLink = result => {
    if (projectKey) return `/projects/${projectKey}/runs/${result.runId}/results`;
    return `/runs/${result.runId}/results`;
  };

  const sortIndicator = (sortBy) => {
    if (filters.sortBy !== sortBy) return '↕';
    return filters.sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-2">
              <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
              {projectKey && <li className="breadcrumb-item"><Link to={breadcrumbBase}>{project?.name || 'Project'}</Link></li>}
              {projectKey && <li className="breadcrumb-item"><Link to={`${breadcrumbBase}/runs`}>Test Runs</Link></li>}
              {!projectKey && <li className="breadcrumb-item"><Link to="/runs">Test Runs</Link></li>}
              <li className="breadcrumb-item active">Results</li>
            </ol>
          </nav>
          <h2 className="mb-1">🧪 {scopeLabel}</h2>
          <div className="text-muted small">
            {runId
              ? 'Inspect and sort the individual case outcomes for this run.'
              : 'Browse result history across runs with workspace-level filters and sorting.'}
          </div>
        </div>
      </div>

      {message && <div className="alert alert-success py-2">{message}</div>}

      <div className="row g-3 mb-4">
        <StatCard label="Visible Results" value={summary.total} tone="primary" />
        <StatCard label="Passed" value={summary.passed} tone="success" />
        <StatCard label="Failed" value={summary.failed} tone="danger" />
        <StatCard label="Blocked" value={summary.blocked} tone="warning" />
        <StatCard label="Skipped" value={summary.skipped} tone="dark" />
        <StatCard label="Not Run" value={summary.notRun} tone="secondary" />
      </div>

      <div className="card mb-4">
        <div className="card-header bg-white border-bottom-0 pb-0">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-xl-4 col-lg-5 col-md-6">
              <div className="small text-muted mb-1">Search</div>
              <input className="form-control form-control-sm" value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="Run, case, user, build..." />
            </div>
            <div className="col-xl-2 col-lg-3 col-md-3">
              <div className="small text-muted mb-1">Result</div>
              <select className="form-select form-select-sm" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                {RESULT_STATUSES.map(value => <option key={value} value={value}>{value === 'all' ? 'All Results' : value.replaceAll('_', ' ')}</option>)}
              </select>
            </div>
            <div className="col-xl-2 col-lg-2 col-md-3">
              <div className="small text-muted mb-1">Rows</div>
              <select className="form-select form-select-sm" value={filters.limit} onChange={e => setFilter('limit', Number(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="col-xl-4 col-lg-12 d-flex gap-2 flex-wrap justify-content-xl-end">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowAdvancedFilters(prev => !prev)}>
                {showAdvancedFilters ? 'Hide Filters' : 'More Filters'}
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={resetFilters}>Reset</button>
              <button className="btn btn-outline-success btn-sm" onClick={exportCsv} disabled={exporting || loading}>
                {exporting ? 'Exporting…' : '⬇️ Export CSV'}
              </button>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="border-top pt-3">
              <div className="row g-2 align-items-end mb-2">
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Run Status</div>
                  <select className="form-select form-select-sm" value={filters.runStatus} onChange={e => setFilter('runStatus', e.target.value)}>
                    {RUN_STATUSES.map(value => <option key={value} value={value}>{value === 'all' ? 'All Runs' : value.replaceAll('_', ' ')}</option>)}
                  </select>
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Run Type</div>
                  <select className="form-select form-select-sm" value={filters.runType} onChange={e => setFilter('runType', e.target.value)}>
                    {RUN_TYPES.map(value => <option key={value} value={value}>{value === 'all' ? 'All Types' : value}</option>)}
                  </select>
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Executed By</div>
                  <input className="form-control form-control-sm" value={filters.executedBy} onChange={e => setFilter('executedBy', e.target.value)} placeholder="Username" />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Build</div>
                  <input className="form-control form-control-sm" value={filters.buildNumber} onChange={e => setFilter('buildNumber', e.target.value)} placeholder="301" />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Environment</div>
                  <input className="form-control form-control-sm" value={filters.environment} onChange={e => setFilter('environment', e.target.value)} placeholder="staging" />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Platform</div>
                  <input className="form-control form-control-sm" value={filters.platform} onChange={e => setFilter('platform', e.target.value)} placeholder="iOS, Web" />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">Browser</div>
                  <input className="form-control form-control-sm" value={filters.browser} onChange={e => setFilter('browser', e.target.value)} placeholder="Chrome" />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">App Version</div>
                  <input className="form-control form-control-sm" value={filters.appVersion} onChange={e => setFilter('appVersion', e.target.value)} placeholder="2.4.1" />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">From</div>
                  <input type="date" className="form-control form-control-sm" value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)} />
                </div>
                <div className="col-xl-2 col-lg-3 col-md-4">
                  <div className="small text-muted mb-1">To</div>
                  <input type="date" className="form-control form-control-sm" value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)} />
                </div>
              </div>

              <div className="row g-2 align-items-end">
                <div className="col-xl-4 col-md-6">
                  <div className="small text-muted mb-1">Saved Filters</div>
                  <select className="form-select form-select-sm" value={selectedPreset} onChange={e => applyPreset(e.target.value)}>
                    <option value="">Select a saved preset…</option>
                    {savedPresets.map(preset => (
                      <option key={preset.id} value={preset.id}>{preset.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-xl-4 col-md-6">
                  <div className="small text-muted mb-1">Preset Name</div>
                  <input className="form-control form-control-sm" value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="e.g. Failed iOS smoke runs" />
                </div>
                <div className="col-xl-4 col-md-12 d-flex gap-2 align-items-end justify-content-xl-end">
                  <button className="btn btn-primary btn-sm flex-grow-1 flex-xl-grow-0" onClick={savePreset}>💾 Save Current Filters</button>
                  <button className="btn btn-outline-danger btn-sm" onClick={deletePreset} disabled={!selectedPreset}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : results.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5 text-muted">
            <div style={{ fontSize: '2.5rem' }}>🧪</div>
            <div className="fw-semibold mt-2">No results match these filters.</div>
            <div className="small">Try a different status, date range, or sort option.</div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => toggleSort('testCaseTitle')}>
                      Test Case {sortIndicator('testCaseTitle')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => toggleSort('status')}>
                      Status {sortIndicator('status')}
                    </button>
                  </th>
                  {!runId && <th>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => toggleSort('runName')}>
                      Run {sortIndicator('runName')}
                    </button>
                  </th>}
                  {!projectKey && <th>Project</th>}
                  <th>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => toggleSort('executedBy')}>
                      Executed By {sortIndicator('executedBy')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => toggleSort('executedAt')}>
                      Executed At {sortIndicator('executedAt')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold" onClick={() => toggleSort('buildNumber')}>
                      Build {sortIndicator('buildNumber')}
                    </button>
                  </th>
                  <th>Configuration</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map(result => {
                  const statusMeta = STATUS_META[result.status] || STATUS_META.not_started;
                  return (
                    <tr key={result.id}>
                      <td>
                        <div className="fw-semibold">{result.testCaseTitle}</div>
                        <div className="small text-muted d-flex gap-2 flex-wrap">
                          {result.testCaseKey && <span>{result.testCaseKey}</span>}
                          {result.comment && <span title={result.comment}>💬 {result.comment.slice(0, 50)}{result.comment.length > 50 ? '…' : ''}</span>}
                          {result.jiraIssueKey && <span>🐞 {result.jiraIssueKey}</span>}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                      </td>
                      {!runId && (
                        <td>
                          <div className="fw-semibold">{result.runName}</div>
                          <div className="small text-muted">{result.runType} • {result.runStatus}</div>
                        </td>
                      )}
                      {!projectKey && <td>{result.projectName || '—'}</td>}
                      <td>
                        <div>{result.executedByName || '—'}</div>
                        {result.assignedToName && <div className="small text-muted">Assigned: {result.assignedToName}</div>}
                      </td>
                      <td>{formatDate(result.executedAt)}</td>
                      <td>
                        <div>{result.buildNumber || '—'}</div>
                        {result.appVersion && <div className="small text-muted">v{result.appVersion}</div>}
                      </td>
                      <td>
                        <div className="small d-flex gap-1 flex-wrap">
                          {result.platform && <span className="badge bg-light text-dark border">{result.platform}</span>}
                          {result.environment && <span className="badge bg-light text-dark border">{result.environment}</span>}
                          {result.browser && <span className="badge bg-light text-dark border">{result.browser}</span>}
                          {result.deviceName && <span className="badge bg-light text-dark border">{result.deviceName}</span>}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link to={getRunLink(result)} className="btn btn-sm btn-outline-primary">Run</Link>
                          {!runId && <Link to={getRunResultsLink(result)} className="btn btn-sm btn-outline-secondary">Results</Link>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card-body border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="small text-muted">
              Showing page {pagination.page} of {Math.max(1, pagination.pages)} • {pagination.total} result{pagination.total !== 1 ? 's' : ''}
            </div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={pagination.page <= 1} onClick={() => setFilter('page', filters.page - 1)}>← Previous</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pagination.page >= pagination.pages} onClick={() => setFilter('page', filters.page + 1)}>Next →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
